import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import ConfirmModal from './ConfirmModal';
import NoticeModal from './NoticeModal';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SubcontractorsPage = () => {
    const [panels, setPanels] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [newPanel, setNewPanel] = useState({ name: '', teminat: '', panelId: '' });
    const _role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
    const isYonetimRole = _role === 'yonetim' || _role === 'master_admin';
    const isAdminRole = isYonetimRole || _role === 'admin';

    // Düzenle modalı
    const [editPanel, setEditPanel] = useState(null); // düzenlenen panelin bir kopyası

    const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null, danger: false });
    const askConfirm = (message, onConfirm, danger = false) => setConfirmState({ open: true, message, onConfirm, danger });
    const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null, danger: false });
    const [notice, setNotice] = useState({ open: false, message: '', danger: false });
    const showNotice = (message, danger = false) => setNotice({ open: true, message, danger });

    const notifyUpdate = () => {
        window.dispatchEvent(new Event('panelsUpdated'));
    };

    useEffect(() => {
        document.title = 'PayzoPay - Departman Yönetimi';
        fetchPanels();
    }, []);

    const fetchPanels = () => {
        api.getPanels()
            .then(res => setPanels(res.data))
            .catch(err => console.error(err));
    };

    const handleAddPanel = async (e) => {
        e.preventDefault();
        try {
            await api.createPanel(newPanel);
            fetchPanels();
            setShowModal(false);
            setNewPanel({ name: '', teminat: '', panelId: '' });
            notifyUpdate();
        } catch (error) {
            showNotice('Departman eklenirken hata: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const togglePanelStatus = async (id, currentStatus) => {
        try {
            await api.updatePanel(id, { isActive: !currentStatus });
            fetchPanels();
            notifyUpdate();
        } catch (error) {
            showNotice('Durum güncellenemedi.', true);
        }
    };

    const deletePanel = (id, name) => {
        askConfirm(`"${name}" departmanını silmek istediğinize emin misiniz?`, async () => {
            try {
                await api.deletePanel(id);
                fetchPanels();
                notifyUpdate();
            } catch (error) {
                showNotice('Silme işlemi başarısız.', true);
            }
        }, true);
    };

    // Düzenle modalını aç
    const openEdit = (panel) => {
        setEditPanel({
            _id: panel._id,
            panelId: panel.panelId,
            name: panel.name || '',
            teminat: panel.teminat || '',
            dailyLimit: panel.dailyLimit ?? '',
            kasaLimit: panel.kasaLimit ?? '',
            kasa: panel.kasa ?? panel.kasaBalance ?? 0,
            devir: panel.devir ?? 0,
            todayDeposit: panel.todayDeposit ?? 0,
            todayWithdrawal: panel.todayWithdrawal ?? 0,
            todayTeslim: panel.todayTeslim ?? 0,
            todayKomisyon: panel.todayKomisyon ?? 0,
            commissionRate: panel.commissionRate ?? '',
            ignoreDailyLimit: !!panel.ignoreDailyLimit,
            suspendedUntil: panel.suspendedUntil || null
        });
    };

    const handleEditChange = (field, value) => {
        setEditPanel(prev => ({ ...prev, [field]: value }));
    };

    const saveEdit = async () => {
        if (!editPanel) return;
        try {
            const payload = {
                name: editPanel.name,
                teminat: editPanel.teminat,
                kasaLimit: editPanel.kasaLimit
            };
            if (isYonetimRole) {
                payload.dailyLimit = editPanel.dailyLimit;
                payload.commissionRate = editPanel.commissionRate;
                payload.ignoreDailyLimit = editPanel.ignoreDailyLimit;
            }
            await api.updatePanel(editPanel._id, payload);
            setEditPanel(null);
            fetchPanels();
            notifyUpdate();
        } catch (error) {
            showNotice('Ayarlar güncellenemedi: ' + (error.response?.data?.error || error.message), true);
        }
    };

    const handleResetSuspend = () => {
        if (!editPanel) return;
        askConfirm('Askı süresini sıfırlamak istediğinize emin misiniz?', async () => {
            try {
                await api.updatePanel(editPanel._id, { suspendedUntil: null });
                setEditPanel(prev => prev ? { ...prev, suspendedUntil: null } : prev);
                fetchPanels();
                notifyUpdate();
            } catch (error) {
                showNotice('Askı sıfırlanamadı.', true);
            }
        });
    };

    const infoLabel = { fontSize: '0.85em', opacity: 0.7 };
    const modalInput = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-color)', boxSizing: 'border-box' };

    return (
        <div style={{ padding: '20px', color: 'var(--text-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ margin: 0 }}>Departman Yönetimi</h2>
                {isAdminRole && (
                <button
                    onClick={() => setShowModal(true)}
                    style={{ padding: '15px 20px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Departman Ekle
                </button>
                )}
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
                {panels.map(panel => {
                    const isSuspended = panel.suspendedUntil && new Date(panel.suspendedUntil) > new Date();
                    return (
                        <div key={panel._id} style={{
                            background: panel.isActive ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                            border: `1px solid ${panel.isActive ? '#28a745' : '#dc3545'}`,
                            padding: '20px',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: 'var(--shadow)'
                        }}>
                            <div>
                                <strong style={{ fontSize: '1.2em', display: 'block', marginBottom: '5px' }}>{panel.name || panel.panelId}</strong>
                                <div style={{ fontSize: '0.9em', opacity: 0.7 }}>ID: {panel.panelId}</div>
                                <div style={{ marginTop: '5px', fontWeight: 'bold', color: panel.isActive ? '#28a745' : '#dc3545' }}>
                                    {panel.isActive ? '● Aktif' : '○ Pasif'}
                                </div>
                            </div>

                            {/* Orta kısım: özet bilgiler (salt okunur) */}
                            <div style={{ flex: 1, margin: '0 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                                <div><div style={infoLabel}>Teminat Notu</div><div style={{ fontWeight: 'bold' }}>{panel.teminat || '-'}</div></div>
                                <div><div style={infoLabel}>Günlük Limit</div><div style={{ fontWeight: 'bold' }}>{fmt(panel.dailyLimit)}</div></div>
                                <div><div style={infoLabel}>Günün Yatırımı</div><div style={{ fontWeight: 'bold' }}>{fmt(panel.todayDeposit)}</div></div>
                                <div><div style={infoLabel}>Kasa Limiti</div><div style={{ fontWeight: 'bold' }}>{fmt(panel.kasaLimit)}</div></div>
                                <div><div style={infoLabel}>Kasa</div><div style={{ fontWeight: 'bold' }}>{fmt(panel.kasa)}</div></div>
                                <div><div style={infoLabel}>Devir</div><div style={{ fontWeight: 'bold' }}>{fmt(panel.devir)}</div></div>
                                {isYonetimRole && (
                                    <div><div style={infoLabel}>Komisyon Oranı</div><div style={{ fontWeight: 'bold' }}>%{panel.commissionRate || 0}</div></div>
                                )}
                                <div><div style={infoLabel}>Limiti Aşabilsin</div><div style={{ fontWeight: 'bold' }}>{panel.ignoreDailyLimit ? 'Evet' : 'Hayır'}</div></div>
                                {isSuspended && (
                                    <div style={{ color: '#dc3545' }}><div style={infoLabel}>Askıda</div><div style={{ fontWeight: 'bold', fontSize: '0.85em' }}>{new Date(panel.suspendedUntil).toLocaleTimeString()}'e kadar</div></div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                {isAdminRole && (
                                <button
                                    onClick={() => openEdit(panel)}
                                    style={{ padding: '8px 16px', background: '#007bff', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#fff', fontWeight: 'bold' }}
                                >
                                    Düzenle
                                </button>
                                )}
                                <button
                                    onClick={() => togglePanelStatus(panel._id, panel.isActive)}
                                    style={{ padding: '8px 16px', background: panel.isActive ? '#ffc107' : '#28a745', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#000', fontWeight: 'bold' }}
                                >
                                    {panel.isActive ? 'Pasife Al' : 'Aktif Et'}
                                </button>
                                {isAdminRole && (
                                <button
                                    onClick={() => deletePanel(panel._id, panel.name || panel.panelId)}
                                    style={{ padding: '8px 16px', background: '#dc3545', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#fff', fontWeight: 'bold' }}
                                >
                                    Sil
                                </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Departman Ekleme Modalı */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '400px', color: 'var(--text-color)' }}>
                        <h3>Yeni Departman Ekle</h3>
                        <form onSubmit={handleAddPanel}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Departman Adı / Tanımı (Harf ve Rakam)</label>
                                <input type="text" placeholder="Örn: 1A, Departman Alfa, Finans-1, 102" value={newPanel.name} onChange={(e) => setNewPanel({ ...newPanel, name: e.target.value })} required style={modalInput} />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Departman Kodu / ID <span style={{ fontSize: '0.8em', opacity: 0.7 }}>(İsteğe bağlı, harf/rakam örn: D-1A)</span></label>
                                <input type="text" placeholder="Boş bırakılırsa sistem otomatik üretir" value={newPanel.panelId || ''} onChange={(e) => setNewPanel({ ...newPanel, panelId: e.target.value })} style={modalInput} />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Teminat Notu / Miktarı</label>
                                <input type="text" placeholder="Örn: 500.000 TL" value={newPanel.teminat} onChange={(e) => setNewPanel({ ...newPanel, teminat: e.target.value })} style={modalInput} />
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ marginRight: '10px', padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Ekle</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Departman Düzenleme Modalı */}
            {editPanel && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '24px', borderRadius: '8px', width: '90%', maxWidth: '440px', color: 'var(--text-color)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ marginTop: 0 }}>Departman Düzenle — {editPanel.name || editPanel.panelId}</h3>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Departman Adı / Tanımı (Harf ve Rakam)</label>
                            <input type="text" placeholder="Örn: 1A, Departman Alfa, Finans-1, 102" value={editPanel.name} onChange={(e) => handleEditChange('name', e.target.value)} style={modalInput} />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Teminat Notu / Miktarı</label>
                            <input type="text" placeholder="Örn: 500.000 TL" value={editPanel.teminat} onChange={(e) => handleEditChange('teminat', e.target.value)} style={modalInput} />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>
                                Günlük Limit {!isYonetimRole && <span style={{ fontSize: '0.8em', color: '#dc3545' }}>(Yalnızca Yönetim)</span>}
                            </label>
                            <input type="number" value={editPanel.dailyLimit} onChange={(e) => handleEditChange('dailyLimit', e.target.value)} disabled={!isYonetimRole} style={{ ...modalInput, opacity: isYonetimRole ? 1 : 0.6, cursor: isYonetimRole ? 'text' : 'not-allowed' }} />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Kasa Limiti</label>
                            <input type="number" value={editPanel.kasaLimit} onChange={(e) => handleEditChange('kasaLimit', e.target.value)} style={modalInput} />
                        </div>
                        {isYonetimRole && (
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Komisyon Oranı (%)</label>
                                <input type="number" step="0.01" value={editPanel.commissionRate} onChange={(e) => handleEditChange('commissionRate', e.target.value)} style={modalInput} />
                            </div>
                        )}
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isYonetimRole ? 'pointer' : 'not-allowed' }}>
                                <input type="checkbox" checked={editPanel.ignoreDailyLimit} onChange={(e) => handleEditChange('ignoreDailyLimit', e.target.checked)} disabled={!isYonetimRole} />
                                Limiti Aşabilsin {!isYonetimRole && <span style={{ fontSize: '0.8em', color: '#dc3545' }}>(Yalnızca Yönetim)</span>}
                            </label>
                        </div>

                        {/* Kasa dökümü (hesaplanan: devir + yatırım − çekim − teslim − komisyon) */}
                        <div style={{ marginTop: '10px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Kasa Dökümü (bugün)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.9em' }}>
                                <div><span style={infoLabel}>Devir (dün): </span>{fmt(editPanel.devir)}</div>
                                <div><span style={infoLabel}>Yatırım: </span>{fmt(editPanel.todayDeposit)}</div>
                                <div><span style={infoLabel}>Çekim: </span>{fmt(editPanel.todayWithdrawal)}</div>
                                <div><span style={infoLabel}>Teslim: </span>{fmt(editPanel.todayTeslim)}</div>
                                <div><span style={infoLabel}>Komisyon: </span>{fmt(editPanel.todayKomisyon)}</div>
                                <div style={{ fontWeight: 'bold' }}><span style={infoLabel}>Kasa: </span>{fmt(editPanel.kasa)}</div>
                            </div>
                        </div>

                        {editPanel.suspendedUntil && new Date(editPanel.suspendedUntil) > new Date() && (
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#dc3545' }}>
                                <span style={{ fontSize: '0.9em' }}>Askıda ({new Date(editPanel.suspendedUntil).toLocaleTimeString()}'e kadar)</span>
                                <button onClick={handleResetSuspend} style={{ padding: '6px 12px', background: '#ffc107', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#000', fontWeight: 'bold' }}>Askıyı Sıfırla</button>
                            </div>
                        )}

                        <div style={{ textAlign: 'right', marginTop: '20px' }}>
                            <button type="button" onClick={() => setEditPanel(null)} style={{ marginRight: '10px', padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>İptal</button>
                            <button type="button" onClick={saveEdit} style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Kaydet</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={confirmState.open}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={() => { if (confirmState.onConfirm) confirmState.onConfirm(); closeConfirm(); }}
                onCancel={closeConfirm}
            />

            <NoticeModal
                open={notice.open}
                message={notice.message}
                danger={notice.danger}
                onClose={() => setNotice({ open: false, message: '', danger: false })}
            />
        </div>
    );
};

export default SubcontractorsPage;
